import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, BarChart3, Trophy, Calendar, Users, PieChart, CheckCircle2, Zap, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import heroImage from "@/assets/landing-hero.jpg";
import BrandLogo from "@/components/BrandLogo";

const LandingPage = () => {
  const features = [
    {
      icon: FileText,
      title: "Gestion des Appels d'Offres",
      description: "Centralisez et suivez tous vos appels d'offres en un seul endroit. Gérez le cycle complet de réponse aux AO."
    },
    {
      icon: BarChart3,
      title: "Analyse IA des documents",
      description: "Évaluation automatique des candidatures avec scoring intelligent et extraction des compétences clés."
    },
    {
      icon: Trophy,
      title: "Ranking et Comparaison",
      description: "Classement intelligent des sociétés et candidats avec visualisations avancées (radar, heatmap, graphiques)."
    },
    {
      icon: Calendar,
      title: "Planning Gantt",
      description: "Visualisation chronologique des projets et suivi des échéances en temps réel."
    },
    {
      icon: Users,
      title: "Gestion Multi-utilisateurs",
      description: "Collaboration entre équipes avec système de permissions granulaires et gestion des rôles."
    },
    {
      icon: PieChart,
      title: "Reporting Avancé",
      description: "Statistiques détaillées, tableaux de bord personnalisables et analyses approfondies de vos données."
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: "Gain de temps considérable",
      description: "Automatisez l'analyse des CV et la gestion des candidatures pour vous concentrer sur l'essentiel."
    },
    {
      icon: CheckCircle2,
      title: "Décisions éclairées",
      description: "Scores et comparaisons objectives pour sélectionner les meilleurs profils en toute confiance."
    },
    {
      icon: Shield,
      title: "Sécurité et conformité",
      description: "Gestion sécurisée des données avec contrôle d'accès et traçabilité complète."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="container mx-auto flex items-center justify-between px-4 py-3">
          <BrandLogo size="sm" subtitle="Plateforme d'analyse" />
          <Link to="/auth">
            <Button variant="default">Connexion</Button>
          </Link>
        </div>
      </header>

      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="grid items-center gap-10 md:grid-cols-2">
          <div className="space-y-6">
            <Badge variant="secondary" className="w-fit">
              Plateforme de gestion d'appels d'offres
            </Badge>
            <h1 className="text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
              Analysez vos AO avec une interface plus claire et plus rapide
            </h1>
            <p className="text-lg text-muted-foreground md:text-xl">
              Centralisez les dossiers, évaluez les candidatures et pilotez les décisions avec un workflow unique.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link to="/auth">
                <Button size="lg" className="w-full sm:w-auto">
                  Se connecter
                </Button>
              </Link>
              <Link to="/home">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  Voir l'application
                </Button>
              </Link>
            </div>
          </div>
          <div>
            <img
              src={heroImage}
              alt="Dashboard de gestion d'appels d'offres avec analyse IA"
              className="h-auto w-full rounded-xl border border-border shadow-xl"
            />
          </div>
        </div>
      </section>

      <section className="container mx-auto my-12 rounded-3xl bg-muted/30 px-4 py-16">
        <div className="mb-12 space-y-4 text-center">
          <Badge variant="secondary" className="w-fit mx-auto">
            Fonctionnalités
          </Badge>
          <h2 className="text-3xl font-bold md:text-4xl">Un socle complet pour gérer vos AO</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Une suite complète d'outils pour optimiser votre processus de réponse aux appels d'offres
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card
              key={index}
              className="border-border/60 transition-colors hover:border-primary/40"
            >
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-xl">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="container mx-auto px-4 py-16">
        <div className="text-center mb-12 space-y-4">
          <Badge variant="secondary" className="w-fit mx-auto">
            Avantages
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold">
            Pourquoi choisir notre plateforme ?
          </h2>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="space-y-4 rounded-xl border border-border/60 bg-card px-4 py-6 text-center"
            >
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <benefit.icon className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold">{benefit.title}</h3>
              <p className="text-muted-foreground">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container mx-auto px-4 py-16">
        <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-background border-primary/20">
          <CardContent className="py-12 text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">
              Prêt à optimiser vos appels d'offres ?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Rejoignez les équipes qui utilisent l'IA pour améliorer leur processus de sélection
            </p>
            <Link to="/auth">
              <Button size="lg" className="mt-4">
                Commencer maintenant
              </Button>
            </Link>
          </CardContent>
        </Card>
      </section>

      <footer className="border-t mt-16">
        <div className="container mx-auto px-4 py-8 text-center text-muted-foreground">
          <p>&copy; 2025 AO Manager. Plateforme de gestion d'appels d'offres avec IA.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
